// SPDX-License-Identifier: MIT
/**
 * ui-cta (closing, 4–6 beats). The brand lands in the middle of the frame with its tagline, a large
 * call-to-action button springs in below, and a pointer glides onto it and clicks on the downbeat
 * (beat 2, or 3 from six beats on: put the impact cue there). The button gives under the click,
 * a ripple spreads, and with a `url` the button opens out into the brand's address.
 *
 * Same logo handling as the other lockups: a symbol gets the name set beside it, a logo that
 * contains the name is used alone, and without a logo the name is set large. The button text is a
 * claim ("Start free" promises something) and must come from the brand's copy or facts.
 */
import * as z from "zod/mini";
import { font } from '../brand.ts';
import { clamp } from '../core/math.ts';
import * as kit from '../kit/index.ts';
import type { Box } from '../kit/path.ts';
import { DESIGN_H, DESIGN_W, type SceneTiming, defineScene } from '../scene.ts';
import { displayTracking, fitLine, logoHasWordmark, markBox, sceneBeats } from './lib.ts';
const { ui } = kit;
const CX = DESIGN_W / 2;
const BTN_H = 116;
const BTN_PX = 44;
const GAP = 36;
/** The click lands on a whole beat, leaving the address time to be read. */
const clickBeat = ({ beats }: Pick<SceneTiming, 'beats'>) => (beats >= 6 ? 3 : 2);
export default defineScene({
    id: 'ui-cta',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        /** The button: the brand's own call to action. */
        cta: z.string().check(z.maxLength(28)),
        /** What the button opens out into after the click. */
        url: z.optional(z.string().check(z.maxLength(40))),
        tagline: z.optional(z.string().check(z.maxLength(60))),
        /** Name set next to a symbol-only logo (default: the brand name). */
        wordmark: z.optional(z.string().check(z.maxLength(24))),
    }),
    glyphs: (p) => (p.wordmark ?? '') + p.cta + (p.url ?? '') + (p.tagline ?? ''),
    claims: (p) => [p.cta, ...(p.tagline ? [p.tagline] : [])],
    motion: (_p, env) => (env.localBeat < 1.4 ? 0.5 : 0.25),
    audioEvents: (p, timing) => {
        const c = clickBeat(timing);
        return [
            { beat: c, sfx: 'tick' as const, gain: 0.8 },
            ...(p.url ? [{ beat: c + 0.3, sfx: 'blip' as const, gain: 0.4 }] : []),
        ];
    },
    post: (_p, env) => (env.style.background === 'dark' ? { bloom: 0.3, lens: 0 } : { bloom: 0.25, lens: 0 }),
    draw(ctx, props, env) {
        const tk = ui.tokens(env);
        const brand = env.brand;
        const logo = brand.logo;
        const L = sceneBeats(env);
        const CLICK = clickBeat({ beats: L });
        ctx.fillStyle = tk.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        if (tk.dark)
            kit.fx.pool(ctx, CX, 640, 1000, tk.accent, 0.07);
        kit.camera.zoom(ctx, 1 + 0.025 * kit.beat.seg(env, 0, L, kit.curves.silk), CX, DESIGN_H / 2);
        // --- Layout: brand, tagline and button stacked on the centre line --------------------------------
        const mode = !logo ? 'text' : logoHasWordmark(brand) ? 'logo' : 'lockup';
        const word = mode === 'logo' ? '' : (props.wordmark ?? brand.name);
        const weight = mode === 'text' ? 800 : 700;
        const tagH = props.tagline ? 96 : 0;
        const brandH = mode === 'logo' ? 170 : mode === 'text' ? 150 : 120;
        const top = (DESIGN_H - (brandH + 56 + tagH + BTN_H)) / 2;
        const base = top + brandH;
        let wordPx = 0;
        let markB: Box = { x: 0, y: 0, w: 0, h: 0 };
        if (mode === 'lockup') {
            wordPx = fitLine(ctx, brand, word, weight, 128, 1000);
            markB = markBox(brand, 0, 0, wordPx * 2.4, wordPx * 1.05);
        }
        else if (mode === 'text')
            wordPx = fitLine(ctx, brand, word, weight, 168, 1500);
        else
            markB = markBox(brand, 0, 0, 1000, brandH);
        let ww = 0;
        if (word) {
            ctx.font = font(brand, weight, wordPx);
            ctx.letterSpacing = displayTracking(word, wordPx);
            ww = ctx.measureText(word).width;
            ctx.letterSpacing = '0px';
        }
        const lockW = mode === 'logo' ? markB.w : mode === 'lockup' ? markB.w + GAP + ww : ww;
        const left = CX - lockW / 2;
        if (mode !== 'text')
            markB = { ...markB, x: left, y: base - markB.h };
        // --- The brand rises into place -------------------------------------------------------------------
        const rise = (b0: number) => {
            const k = kit.beat.seg(env, b0, b0 + 0.9, kit.curves.glide);
            return { k, dy: (1 - k) * 36 };
        };
        if (logo && mode !== 'text') {
            const { k, dy } = rise(0);
            if (k > 0) {
                ctx.save();
                ctx.globalAlpha *= k;
                ctx.translate(0, dy);
                for (const part of logo.parts) {
                    ctx.fillStyle = part.fill ?? tk.accent;
                    kit.path.tracePath(ctx, kit.path.fit(part.subpaths, markB, logo.bounds));
                    ctx.fill(part.fillRule);
                }
                ctx.restore();
            }
            env.report?.element('logo', [markB.x, markB.y, markB.w, markB.h]);
        }
        else
            env.report?.element('logo', [left, base - wordPx * 0.75, ww, wordPx * 0.75]);
        if (word) {
            const { k, dy } = rise(mode === 'text' ? 0 : 0.15);
            if (k > 0) {
                ctx.save();
                ctx.globalAlpha *= k;
                ctx.font = font(brand, weight, wordPx);
                ctx.letterSpacing = displayTracking(word, wordPx);
                ctx.fillStyle = tk.text;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'alphabetic';
                ctx.fillText(word, mode === 'lockup' ? left + markB.w + GAP : left, base + dy - (mode === 'lockup' ? wordPx * 0.12 : 0));
                ctx.restore();
            }
        }
        if (props.tagline) {
            const { k, dy } = rise(0.45);
            if (k > 0) {
                ctx.save();
                ctx.globalAlpha *= k;
                ui.fitLabel(ctx, tk, props.tagline, 1500, 46, 500, 30);
                ui.label(ctx, props.tagline, CX, base + 56 + 34 + dy, tk.muted, 'center');
                ctx.restore();
            }
        }
        // --- The button: springs in, is clicked on the beat, opens into the address ---------------------
        const btnY = base + 56 + tagH;
        ctx.font = tk.font(BTN_PX, 600);
        const ctaW = ctx.measureText(props.cta).width + 130 + 56;
        ctx.font = tk.font(BTN_PX, 500);
        const urlW = props.url ? ctx.measureText(props.url).width + 120 + 52 : ctaW;
        const btn: kit.ui.Shape = { x: CX - ctaW / 2, y: btnY, w: ctaW, h: BTN_H, r: BTN_H / 2, fill: tk.accent };
        const pill: kit.ui.Shape = {
            x: CX - urlW / 2,
            y: btnY,
            w: urlW,
            h: BTN_H,
            r: BTN_H / 2,
            fill: tk.accentSoft,
        };
        const appear = kit.curves.spring(kit.beat.seg(env, 0.8, 1.5));
        if (appear <= 0)
            return;
        const open = props.url ? kit.curves.spring(kit.beat.seg(env, CLICK + 0.12, CLICK + 0.75)) : 0;
        const pts: kit.ui.Waypoint[] = [
            { beat: 0.9, x: CX + 760, y: DESIGN_H + 80 },
            // Pressed in the padding past the arrow, so the hand does not cover the words.
            { beat: CLICK, x: CX + ctaW / 2 - 52, y: btnY + BTN_H * 0.64, click: true },
            { beat: CLICK + 1.6, x: CX + ctaW / 2 + 300, y: DESIGN_H + 80 },
        ];
        const cur = ui.cursorAt(env, pts, { travel: Math.min(1.1, CLICK - 1) });
        const shape = ui.morph(btn, pill, open);
        const grown = ui.scaleBox(shape, (0.82 + 0.18 * appear) * (1 - 0.035 * cur.press * (1 - open)));
        ctx.save();
        ctx.globalAlpha *= clamp(appear * 1.5);
        ui.elevation(ctx, tk, grown, shape.r, 1.2 - 0.6 * cur.press);
        ui.rrect(ctx, grown, shape.r);
        ctx.fillStyle = kit.color.mix(shape.fill, '#000000', 0.14 * cur.press * (1 - open));
        ctx.fill();
        if (open > 0) {
            ctx.save();
            ctx.globalAlpha *= open;
            // The address keeps the button's colour as an edge, so the close still ends on the brand.
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = kit.color.mix(tk.accent, tk.bg, 0.25);
            ctx.stroke();
            ctx.restore();
        }
        // Label out, address in, inside the shape.
        ctx.save();
        ui.rrect(ctx, grown, shape.r);
        ctx.clip();
        const cy = grown.y + grown.h / 2;
        const out = 1 - clamp(open / 0.4);
        if (out > 0) {
            ctx.save();
            ctx.globalAlpha *= out;
            ctx.font = tk.font(BTN_PX, 600);
            const tw = ctx.measureText(props.cta).width;
            const x = CX - (tw + 56) / 2;
            ui.label(ctx, props.cta, x, cy + 1, tk.onAccent);
            // The arrow nudges forward under the click.
            const nudge = kit.beat.hits(env, [CLICK], 5) * 10;
            ui.icon(ctx, 'arrow', x + tw + 20 + nudge, cy - 20, 40, tk.onAccent);
            ctx.restore();
        }
        const inA = clamp((open - 0.3) / 0.7);
        if (props.url && inA > 0) {
            ctx.globalAlpha *= inA;
            ctx.font = tk.font(BTN_PX, 500);
            const tw = ctx.measureText(props.url).width;
            const x = CX - (tw + 52) / 2;
            ui.icon(ctx, 'link', x, cy - 18, 36, tk.accentText);
            ui.label(ctx, props.url, x + 52, cy + 1, tk.text);
        }
        ctx.restore();
        ctx.restore();
        ui.clickRipple(ctx, tk, cur.clickAt[0], cur.clickAt[1], cur.sinceClick);
        ui.cursor(ctx, tk, cur.x, cur.y, { press: cur.press, scale: 1.25 });
    },
});
